import apiService from '../api'

export interface PaginationMeta {
  page: number
  limit: number
  total: number
  totalPages: number
}

export interface OrderFulfillmentApi {
  id: string
  buyContract?: string | null
  saleContract?: string | null
  incoterm?: string | null
  factory?: string | null
  customer?: string | null
  product?: string | null
  forwarder?: string | null
  pol?: string | null
  pod?: string | null
  booking?: string | null
  carrier?: string | null
  vessel?: string | null
  bl?: string | null
  containerNumber?: string | null
  lotBatch?: string | null
  quantity?: string | null
  ncrRef?: string | null
  ncr?: string | null
  deliveryDate?: string | null
  outcome?: 'Accepted' | 'Rejected' | null
  status: 'Pending' | 'Paid' | 'Completed'
  createdAt: string
  updatedAt: string
  stockDispatches?: Array<{
    id: string
    product?: string | null
    lotNumber?: string | null
    qtyDispatched?: string | null
    qtyUnit?: string | null
    customer?: string | null
    destination?: string | null
    dispatchDate?: string | null
    status?: string | null
    dispatchCosts?: string | null
    dispatchCostsCurrency?: string | null
    dispatchInstructionUrl?: string | null
    proofOfDeliveryUrl?: string | null
    transporterInvoiceUrl?: string | null
    salesInvoiceUrl?: string | null
    dispatchNoteImageUrl?: string | null
    warehouse?: {
      id: string
      name: string
    } | null
  }>
}

export interface OrderFulfillmentListResponse {
  orderFulfillments: OrderFulfillmentApi[]
  meta: PaginationMeta
}

export interface GetOrderFulfillmentsParams {
  page?: number
  limit?: number
  status?: string
  outcome?: string
  customer?: string
  search?: string
  startDate?: string
  endDate?: string
}

export async function getOrderFulfillments(
  params?: GetOrderFulfillmentsParams
): Promise<OrderFulfillmentListResponse> {
  const queryParams: Record<string, string> = {}
  if (params) {
    if (params.page) queryParams.page = String(params.page)
    if (params.limit) queryParams.limit = String(params.limit)
    if (params.status) queryParams.status = params.status
    if (params.outcome) queryParams.outcome = params.outcome
    if (params.customer) queryParams.customer = params.customer
    if (params.search) queryParams.search = params.search
    if (params.startDate) queryParams.startDate = params.startDate
    if (params.endDate) queryParams.endDate = params.endDate
  }
  const qs = Object.keys(queryParams).length
    ? new URLSearchParams(queryParams).toString()
    : ''
  const res = await apiService.get<{ success: boolean; data: OrderFulfillmentListResponse }>(
    `/order-fulfillments${qs ? '?' + qs : ''}`
  )
  const data = (res as any)?.data ?? res
  return {
    orderFulfillments: data.orderFulfillments ?? [],
    meta: data.meta ?? { page: 1, limit: params?.limit ?? 10, total: 0, totalPages: 0 },
  }
}

export async function getOrderFulfillmentById(id: string): Promise<OrderFulfillmentApi> {
  const res = await apiService.get<{ success: boolean; data: OrderFulfillmentApi }>(
    `/order-fulfillments/${id}`
  )
  const data = (res as any)?.data ?? res
  return data
}

export interface CreateOrderFulfillmentPayload {
  shipmentId?: string
  buyContract?: string
  saleContract?: string
  incoterm?: string
  factory?: string
  customer?: string
  product?: string
  forwarder?: string
  pol?: string
  pod?: string
  booking?: string
  carrier?: string
  vessel?: string
  bl?: string
  containerNumber?: string
  lotBatch?: string
  quantity?: string
  ncrRef?: string
  deliveryDate?: string
  outcome?: 'Accepted' | 'Rejected'
  status?: 'Pending' | 'Paid' | 'Completed'
}

export async function createOrderFulfillment(
  payload: CreateOrderFulfillmentPayload
): Promise<OrderFulfillmentApi> {
  const res = await apiService.post<{ success: boolean; data: OrderFulfillmentApi }>(
    '/order-fulfillments',
    payload
  )
  const data = (res as any)?.data ?? res
  return data
}

export async function updateOrderFulfillment(
  id: string,
  payload: Partial<CreateOrderFulfillmentPayload>
): Promise<OrderFulfillmentApi> {
  const res = await apiService.put<{ success: boolean; data: OrderFulfillmentApi }>(
    `/order-fulfillments/${id}`,
    payload
  )
  const data = (res as any)?.data ?? res
  return data
}

export async function updateOrderFulfillmentStatus(
  id: string,
  status: 'Pending' | 'Paid' | 'Completed'
): Promise<OrderFulfillmentApi> {
  const res = await apiService.patch<{ success: boolean; data: OrderFulfillmentApi }>(
    `/order-fulfillments/${id}/status`,
    { status }
  )
  const data = (res as any)?.data ?? res
  return data
}

export async function updateOrderFulfillmentOutcome(
  id: string,
  outcome: 'Accepted' | 'Rejected'
): Promise<OrderFulfillmentApi> {
  const res = await apiService.patch<{ success: boolean; data: OrderFulfillmentApi }>(
    `/order-fulfillments/${id}/outcome`,
    { outcome }
  )
  const data = (res as any)?.data ?? res
  return data
}

export async function uploadNCRDocument(
  orderFulfillmentId: string,
  file: File
): Promise<{ fileUrl: string }> {
  const formData = new FormData()
  formData.append('file', file)
  const res = await apiService.instance.post<{ success?: boolean; data?: { fileUrl: string } }>(
    `/order-fulfillments/${orderFulfillmentId}/ncr`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )
  const body = (res as any)?.data
  const payload = body?.data ?? body
  return { fileUrl: payload?.fileUrl ?? '' }
}

export async function deleteOrderFulfillment(id: string): Promise<void> {
  await apiService.delete(`/order-fulfillments/${id}`)
}

/** Base URL for server (no trailing slash, without /api). Used to build file URLs. */
const getServerBase = () => {
  const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'
  // Remove /api suffix to get server base URL since uploads are served at /uploads not /api/uploads
  return apiBase.replace(/\/api\/?$/, '')
}

/** 
 * Fetch the NCR document URL from the backend.
 * This handles both S3 (returns signed URL) and local files.
 */
export async function fetchNCRDocumentUrl(orderFulfillmentId: string): Promise<string> {
  interface NCRUrlResponse {
    success?: boolean;
    data?: { url?: string };
    url?: string;
  }
  const body = await apiService.get<NCRUrlResponse>(`/order-fulfillments/${orderFulfillmentId}/ncr-url`)
  const url = body?.data?.url ?? body?.url ?? ''
  
  // If it's a local file path, prepend the server base
  if (url && url.startsWith('/uploads/')) {
    const base = getServerBase().replace(/\/$/, '')
    return `${base}${url}`
  }
  
  return url
}

/** 
 * URL to view an NCR document in the browser (opens in new tab).
 * @deprecated Use fetchNCRDocumentUrl() instead for proper S3 signed URL support
 */
export function getNCRDocumentUrl(ncrUrl: string): string {
  if (!ncrUrl) return ''
  // If it's already a full URL, return as is
  if (ncrUrl.startsWith('http://') || ncrUrl.startsWith('https://')) {
    return ncrUrl
  }
  // Otherwise, prepend the server base (without /api since uploads are at /uploads)
  const base = getServerBase().replace(/\/$/, '')
  return `${base}${ncrUrl.startsWith('/') ? '' : '/'}${ncrUrl}`
}
